import { useMutation, useQuery } from "@apollo/client";
import { Alert, Card, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Icon, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import Footer from "components/Footer";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import CustomPagination from "components/Pagination";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import DataTable from "components/Tables/DataTable";
import { MessageManager } from "context";
import { ConfirmarContext } from "context/ConfirmarContext";
import { MenuContext } from "context/menuContext";
import { UserContext } from "context/user";
import { getUproColor } from "utils/colors";
import { CAMBIAR_ACTIVO_EVENTO, CONFIGURACION_EVENTOS, ELIMINAR_CONFIGURACION_EVENTO } from "services/configuracionEventos";
import tablaEventos from "./data/tablaEventos";
import EventosMobile from "./components/EventosMobile";
import {
  ITEMS_POR_PAGINA, PERMISO_CONFIGURAR_EVENTOS, RUTA_LISTADO, estadoEvento, eventoFinalizado, mensajeErrorEvento,
} from "./utils";

// El estado (Programado, En curso, Finalizado) depende de la hora: se recalcula
// cada minuto aunque no cambien los datos.
const INTERVALO_ESTADO_MS = 60 * 1000;

const opcionMenu = (titulo, icono, onClick, color = "uproGreen") => (
  <MenuItem onClick={onClick}>
    <SoftBox display="flex" justifyContent="space-between" alignItems="center" width="100%" gap={2}>
      <SoftTypography variant="p">{titulo}</SoftTypography>
      <Icon fontSize="small" sx={{ verticalAlign: "middle", color: getUproColor(color) }}>{icono}</Icon>
    </SoftBox>
  </MenuItem>
);

export default function ConfiguracionEventos() {
  const navigate = useNavigate();
  const { user, verificarAcceso } = useContext(UserContext);
  const { handleSnackbar } = useContext(MessageManager);
  const { confirmar } = useContext(ConfirmarContext);
  const { menu: menuEstado } = useContext(MenuContext);
  const [cambiarActivo] = useMutation(CAMBIAR_ACTIVO_EVENTO);
  const [idCambiandoEstado, setIdCambiandoEstado] = useState(null);
  const autorizado = verificarAcceso(PERMISO_CONFIGURAR_EVENTOS);
  const esAdministrador = user?.tipoUser === "Administrador";
  const [eliminar, { loading: eliminando }] = useMutation(ELIMINAR_CONFIGURACION_EVENTO);
  const [busqueda, setBusqueda] = useState("");
  const [busquedaAplicada, setBusquedaAplicada] = useState("");
  const [page, setPage] = useState(1);
  const [orden, setOrdenState] = useState({ campo: "inicio", orden: "DESC" });
  const [menu, setMenu] = useState({ anchorEl: null, evento: null });
  const [aEliminar, setAEliminar] = useState(null);
  const [eliminarAbierto, setEliminarAbierto] = useState(false);
  const [errorEliminar, setErrorEliminar] = useState("");
  const [, setReloj] = useState(0);

  // Búsqueda, orden y paginado se resuelven en el servidor.
  const { data, loading, error, refetch } = useQuery(CONFIGURACION_EVENTOS, {
    skip: !autorizado,
    fetchPolicy: "network-only",
    variables: {
      search: busquedaAplicada.trim() || null,
      order: { field: orden.campo, order: orden.orden },
      pagination: { page, limit: ITEMS_POR_PAGINA },
    },
  });
  const eventos = data?.configuracionEventosComedor || [];
  const paginas = data?.paginationInfo?.pages || 0;

  useEffect(() => {
    const id = setInterval(() => setReloj((valor) => valor + 1), INTERVALO_ESTADO_MS);
    return () => clearInterval(id);
  }, []);

  // Espera a que el usuario deje de escribir antes de consultar.
  useEffect(() => {
    const id = setTimeout(() => {
      setBusquedaAplicada(busqueda);
      setPage(1);
    }, 400);
    return () => clearTimeout(id);
  }, [busqueda]);

  // Al eliminar el último evento de la última página se vuelve a la anterior.
  useEffect(() => {
    if (paginas > 0 && page > paginas) setPage(paginas);
  }, [page, paginas]);

  const setOrden = (siguiente) => { setOrdenState(siguiente); setPage(1); };
  const cambiarOrden = (campo) => setOrden({
    campo,
    orden: orden.campo === campo && orden.orden === "ASC" ? "DESC" : "ASC",
  });

  const nuevo = () => navigate(`${RUTA_LISTADO}/nuevo`);
  const cerrarMenu = () => setMenu((prev) => ({ ...prev, anchorEl: null }));
  const conEvento = (accion) => () => { const { evento } = menu; cerrarMenu(); accion(evento); };
  // El texto del diálogo se conserva durante la animación de cierre.
  const abrirEliminar = (evento) => { setErrorEliminar(""); setAEliminar(evento); setEliminarAbierto(true); };
  const cerrarEliminar = () => setEliminarAbierto(false);

  const confirmarEliminar = async () => {
    try {
      await eliminar({ variables: { id: String(aEliminar.id) } });
    } catch (e) {
      setErrorEliminar(mensajeErrorEvento(e, "No se pudo eliminar el evento"));
      return;
    }
    cerrarEliminar();
    handleSnackbar("Evento eliminado", "success");
    try {
      await refetch();
    } catch (e) {
      handleSnackbar(mensajeErrorEvento(e, "No se pudo actualizar el listado de eventos"), "error");
    }
  };

  // Mientras se aplica, el badge de ese evento muestra la carga y no abre el menú.
  const aplicarActivo = async (evento, activo) => {
    if (idCambiandoEstado) return;
    setIdCambiandoEstado(String(evento.id));
    try {
      try {
        await cambiarActivo({ variables: { id: String(evento.id), activo } });
      } catch (e) {
        handleSnackbar(mensajeErrorEvento(e, "No se pudo cambiar el estado del evento"), "error");
        return;
      }
      handleSnackbar(`Evento «${evento.nombre}» ${activo ? "activado" : "desactivado"}`, "success");
      try {
        await refetch();
      } catch (e) {
        handleSnackbar(mensajeErrorEvento(e, "No se pudo actualizar el listado de eventos"), "error");
      }
    } finally {
      setIdCambiandoEstado(null);
    }
  };

  // Desactivar un evento en curso corta la venta en el momento: se confirma.
  const elegirActivo = (evento, activo) => {
    if (activo || estadoEvento(evento) !== "En curso") { aplicarActivo(evento, activo); return; }
    confirmar({
      icon: "pause_circle",
      title: "Desactivar evento en curso",
      message: `Si desactiva «${evento.nombre}», el punto de venta del evento deja de vender de inmediato y la app de autogestión vuelve a funcionar normalmente (se quitan los bloqueos del evento). Puede volver a activarlo mientras el evento no termine.`,
      confirmLabel: "Desactivar",
      func: () => aplicarActivo(evento, false),
    });
  };

  // Menú del badge de estado, con el mismo formato que el de grupos de vouchers.
  const abrirMenuEstado = (event, evento) => {
    event.stopPropagation();
    if (idCambiandoEstado) return;
    const finalizado = eventoFinalizado(evento);
    const actual = evento.activo ? "Activo" : "Inactivo";
    const opciones = [
      { name: actual, disabled: true, icon: { icon: "check", color: "success" } },
      evento.activo
        ? { name: "Inactivo", disabled: finalizado, icon: { icon: "pause", color: "warning" }, onClick: () => elegirActivo(evento, false) }
        : { name: "Activo", disabled: finalizado, icon: { icon: "play_arrow", color: "success" }, onClick: () => elegirActivo(evento, true) },
    ];
    if (finalizado) {
      opciones.push({ name: "Evento finalizado: extienda el cierre para reactivarlo", disabled: true, icon: { icon: "info", color: "secondary" } });
    }
    menuEstado({ open: event.currentTarget, align: "right", options: opciones });
  };

  const filas = tablaEventos.getRows(eventos, {
    onEstado: abrirMenuEstado,
    idCambiandoEstado,
    onEditar: (evento) => navigate(`${RUTA_LISTADO}/${evento.id}`),
    onMenu: (e, evento) => setMenu({ anchorEl: e.currentTarget, evento }),
  });

  return (
    <DashboardLayout>
      <DashboardNavbar title="Configuración de eventos" />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <Card>
            <Grid container spacing={2} p={3}>
              <Grid item xs={12}>
                <SoftBox display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }}>
                  <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                    <SoftBox display="flex" alignItems="center" gap={0.5}>
                      <SoftTypography variant="h6">Configuración de eventos</SoftTypography>
                      {esAdministrador && (
                        <Tooltip title="Cómo crear un evento" placement="top">
                          <IconButton size="small" aria-label="Cómo crear un evento" onClick={() => navigate(`${RUTA_LISTADO}/flujo`)}
                            sx={{ color: getUproColor("uproGreen") }}>
                            <Icon>help</Icon>
                          </IconButton>
                        </Tooltip>
                      )}
                    </SoftBox>
                    {autorizado && (
                      <SoftBox display={{ xs: "block", sm: "none" }}>
                        <Tooltip title="Nuevo evento" placement="top">
                          <SoftButton color="primary" circular iconOnly onClick={nuevo}><Icon>add</Icon></SoftButton>
                        </Tooltip>
                      </SoftBox>
                    )}
                  </SoftBox>
                  {autorizado && (
                    <SoftBox display="flex" alignItems="center" justifyContent="end" mt={{ xs: 2, sm: 0 }}>
                      <SoftBox mr={{ xs: 0, sm: 2 }} sx={{ width: { xs: "100%", sm: "auto" } }}>
                        <SoftInput placeholder="Buscar evento..." icon={{ component: "search", direction: "left" }}
                          value={busqueda} onChange={(e) => setBusqueda(e.target.value)} inputProps={{ "aria-label": "Buscar evento" }} />
                      </SoftBox>
                      <SoftBox display={{ xs: "none", sm: "block" }}>
                        <SoftButton color="primary" circular onClick={nuevo}>
                          <Icon>add</Icon>&nbsp;Nuevo evento
                        </SoftButton>
                      </SoftBox>
                    </SoftBox>
                  )}
                </SoftBox>
              </Grid>
            </Grid>
            <SoftBox sx={{
              "& .MuiTableRow-root:not(:last-child)": {
                "& td": { borderBottom: ({ borders: { borderWidth, borderColor } }) => `${borderWidth[1]} solid ${borderColor}` },
              },
            }}>
              {!autorizado ? <SoftBox px={3} pb={3}><Alert severity="error">No tiene permiso para configurar eventos.</Alert></SoftBox>
                : loading ? <Loading />
                  : error ? <SoftBox px={3} pb={3}><Alert severity="error" action={<SoftButton size="small" color="error" variant="text" onClick={() => refetch()}>Reintentar</SoftButton>}>{mensajeErrorEvento(error)}</Alert></SoftBox>
                    : filas.length ? <>
                      <SoftBox display={{ xs: "none", sm: "block" }}>
                        <DataTable columns={tablaEventos.columns} rows={filas} order={orden} setOrder={cambiarOrden} />
                      </SoftBox>
                      <SoftBox display={{ xs: "block", sm: "none" }}>
                        <EventosMobile filas={filas} orden={orden} setOrden={setOrden} />
                      </SoftBox>
                    </>
                      : <SinDatos titulo={busquedaAplicada ? "Sin resultados" : "Todavía no hay eventos"} icono="event"
                        mensaje={busquedaAplicada ? "Ningún evento coincide con la búsqueda." : "Cree uno con «Nuevo evento»."} />}
            </SoftBox>
            {autorizado && !error && (
              <CustomPagination loading={loading} length={eventos.length} total={data?.paginationInfo?.total || 0}
                pages={paginas} page={page} setPage={setPage} noCard />
            )}
          </Card>
        </SoftBox>
      </SoftBox>

      <Menu anchorEl={menu.anchorEl} open={Boolean(menu.anchorEl)} onClose={cerrarMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
        {/* Un evento finalizado se consulta en modo lectura. */}
        {menu.evento && eventoFinalizado(menu.evento)
          ? opcionMenu("Ver", "visibility", conEvento((evento) => navigate(`${RUTA_LISTADO}/${evento.id}`)))
          : opcionMenu("Editar", "edit", conEvento((evento) => navigate(`${RUTA_LISTADO}/${evento.id}`)))}
        {opcionMenu("Duplicar", "content_copy", conEvento((evento) => navigate(`${RUTA_LISTADO}/nuevo?duplicar=${evento.id}`)))}
        {opcionMenu("Eliminar", "delete", conEvento(abrirEliminar), "uproPink")}
      </Menu>
      <Dialog open={eliminarAbierto} onClose={eliminando ? undefined : cerrarEliminar} fullWidth maxWidth="sm"
        TransitionProps={{ onExited: () => setAEliminar(null) }}
        aria-labelledby="eliminar-evento-titulo">
        <DialogTitle id="eliminar-evento-titulo">Eliminar evento</DialogTitle>
        <DialogContent sx={{ overflowWrap: "anywhere" }}>
          <SoftTypography variant="button" component="p">¿Está seguro que quiere eliminar «{aEliminar?.nombre}»?</SoftTypography>
          <SoftTypography variant="caption">Solo se pueden eliminar eventos sin ventas; si tiene ventas, desactívelo desde Editar.</SoftTypography>
          {errorEliminar && <Alert severity="error" sx={{ mt: 2 }}>{errorEliminar}</Alert>}
        </DialogContent>
        <DialogActions>
          <SoftButton variant="outlined" color="dark" circular disabled={eliminando} onClick={cerrarEliminar}>Cancelar</SoftButton>
          <SoftButton color="primary" circular disabled={eliminando} onClick={confirmarEliminar}>{eliminando ? "Eliminando…" : "Eliminar"}</SoftButton>
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}
